import { Combobox } from '@base-ui/react/combobox'
import { MagnifyingGlassIcon, XCircleIcon } from '@phosphor-icons/react'
import clsx from 'clsx'
import { useState } from 'react'
import { CantonCode, CANTONS } from '@/blocks/PriceCalculator/prices'

type Canton = (typeof CANTONS)[number]

export default function CantonCombobox({
  value,
  onChange,
}: {
  value: CantonCode | null
  onChange: (canton: CantonCode | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const selected = CANTONS.find((canton) => canton.code === value) ?? null
  const inputValue = open ? query : (selected?.name ?? '')

  return (
    <Combobox.Root
      items={CANTONS}
      value={selected}
      onValueChange={(canton: Canton | null) => onChange(canton?.code ?? null)}
      inputValue={inputValue}
      onInputValueChange={setQuery}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (nextOpen) setQuery('')
      }}
      itemToStringLabel={(canton) => canton?.name ?? ''}
      isItemEqualToValue={(a, b) => a?.code === b?.code}
    >
      <div className="relative w-full max-w-140">
        <Combobox.Input
          placeholder="Kanton auswählen"
          className={clsx(
            'h-12 w-full rounded-lg border border-gray-300 bg-white pr-16 pl-4',
            'placeholder:text-gray-400 focus:border-blue-700 focus:outline-none'
          )}
        />
        <div className="absolute inset-y-0 right-2 flex items-center">
          {selected ? (
            <Combobox.Clear
              aria-label="Auswahl löschen"
              className="flex text-gray-800"
            >
              <XCircleIcon size={20} weight="bold" />
            </Combobox.Clear>
          ) : (
            <MagnifyingGlassIcon
              size={20}
              weight="bold"
              className="text-gray-800"
            />
          )}
        </div>
      </div>
      <Combobox.Portal>
        <Combobox.Positioner sideOffset={8} align="start" className="z-50">
          <Combobox.Popup
            className={clsx(
              'w-(--anchor-width) overflow-hidden',
              'rounded-lg border border-gray-300 bg-white shadow-xl',
              'data-open:animate-in data-open:fade-in-0% data-open:zoom-in-95%',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
              'data-closed:animate-out data-closed:fade-out-0% data-closed:zoom-out-95%'
            )}
          >
            <Combobox.Empty>
              <p className="px-4 py-3 text-sm text-gray-800">
                Kein Kanton gefunden
              </p>
            </Combobox.Empty>
            <Combobox.List className="max-h-72 divide-y divide-gray-150 overflow-y-auto">
              {(canton: Canton) => (
                <Combobox.Item
                  key={canton.code}
                  value={canton}
                  className="flex h-10 cursor-pointer items-center justify-between px-4 whitespace-nowrap outline-none hover:bg-gray-200 data-highlighted:bg-gray-200 data-selected:font-bold data-selected:text-blue-700"
                >
                  <span>{canton.name}</span>
                  <span className="text-sm text-gray-400 uppercase">
                    {canton.code}
                  </span>
                </Combobox.Item>
              )}
            </Combobox.List>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  )
}
